/**
 * On an org node: may it have its own plan of record in force (migration 919)?
 * The top node always may; any other node only when a workspace admin turns
 * this on. The database checks who may change it; this only reads and flips it.
 * Hidden until the setting can be read (before 919 is applied).
 */
import { useEffect, useState } from 'react';
import { Switch } from '@/components/ds';
import { getTypedSupabaseClient } from '@/lib/typedSupabaseClient';
import { setOrgUnitOwnPlanInForce } from '@/lib/planForceService';

export function OwnPlanInForceSetting({ orgUnitId, isTop }: { orgUnitId: string; isTop: boolean }) {
  const [value, setValue] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setValue(null);
    void getTypedSupabaseClient()
      .from('org_units')
      .select('own_plan_in_force')
      .eq('id', orgUnitId)
      .maybeSingle()
      .then(({ data, error: err }) => {
        if (live && !err && data) setValue(data.own_plan_in_force);
      });
    return () => {
      live = false;
    };
  }, [orgUnitId]);

  if (isTop) {
    return (
      <div>
        <span className="text-[var(--text-ter)]">Plan in force:</span>
        <span className="ml-2 text-[var(--text-sec)]">Always — the top node has its own plan in force.</span>
      </div>
    );
  }
  if (value === null) return null;

  return (
    <div>
      <Switch
        checked={value}
        disabled={saving}
        label="May have its own plan in force"
        description={
          value
            ? 'A plan for this node can be in force here, alongside the plan for the rest of the organization.'
            : 'Off: plans for this node count as plans for the whole organization.'
        }
        onChange={async (next) => {
          setSaving(true);
          setError(null);
          const err = await setOrgUnitOwnPlanInForce(orgUnitId, next);
          setSaving(false);
          if (err) setError(err);
          else setValue(next);
        }}
      />
      {error && <p className="mt-1 text-sm text-[var(--coral-text)]">{error}</p>}
    </div>
  );
}

export default OwnPlanInForceSetting;
